import { AlertCircle, LinkIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogBody, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { HttpError, isBadRequest } from "@/types/error";
import { ExpirationPeriod } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useShareRecording } from "../api/share-recording";
import {
  ShareRecordingFormData,
  shareRecordingSchema,
} from "../types/share-recording-schema";

type ShareRecordingFormProps = {
  recordingId: string;
};

const nbOfDaysOptions = [
  { label: "7 days", value: ExpirationPeriod.SEVEN_DAYS },
  { label: "14 days", value: ExpirationPeriod.FOURTEEN_DAYS },
  { label: "30 days", value: ExpirationPeriod.THIRTY_DAYS },
];

export function ShareRecordingForm(props: ShareRecordingFormProps) {
  const { mutateAsync: shareRecording, isPending } = useShareRecording();
  const { organisation } = useOrganisation();
  const form = useForm<ShareRecordingFormData>({
    resolver: zodResolver(shareRecordingSchema),
    defaultValues: {
      expirationPeriod: ExpirationPeriod.SEVEN_DAYS,
    },
  });

  async function handleShareRecording(data: ShareRecordingFormData) {
    try {
      const result = await shareRecording({
        orgId: organisation.id,
        data: {
          recordingId: props.recordingId,
          expiresAt: data.expirationPeriod,
        },
      });
      await navigator.clipboard.writeText(
        location.hostname + "/shared-recordings/" + result.id,
      );
      toast.success("Copied!");

      console.log(result);
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleShareRecording)}>
        <DialogBody>
          <div className="max-w-56 space-y-4">
            <FormField
              control={form.control}
              name="expirationPeriod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Link expires after</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={form.formState.isSubmitting}
                  >
                    <FormControl>
                      <SelectTrigger className="capitalize">
                        <SelectValue placeholder="Select a pricing plan..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {nbOfDaysOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="capitalize"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {form.formState.errors.root && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unexpected error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter className="sm:justify-end">
          <Button
            type="submit"
            variant="default"
            isLoading={isPending}
            isAnimated
          >
            <LinkIcon className="mr-2 size-4" aria-hidden={true} />
            Copy link
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
